import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Apis } from "constants/apis";
import { getToken } from "utils/token";

export type CheckoutStage = "1h" | "24h" | "72h";

export interface AbandonedCheckoutRecord {
    id: number;
    email: string;
    phone: string | null;
    source: string;
    plan: string | null;
    status: "initiated" | "abandoned" | "converted";
    emailOptOut: boolean;
    email1hSentAt: string | null;
    email24hSentAt: string | null;
    email72hSentAt: string | null;
    wa1hSentAt: string | null;
    wa24hSentAt: string | null;
    wa72hSentAt: string | null;
    convertedAt: string | null;
    conversionType: "organic" | "marketing" | null;
    createdAt: string;
    updatedAt: string;
}

export interface CheckoutCounts {
    initiated: number;
    abandoned: number;
    converted: number;
    marketingConverted: number;
    organicConverted: number;
    marketingReached: number;
    optedOut: number;
    total: number;
    /** Marketing-attributed conversions / users who received any marketing send. */
    conversionRate: string;
}

interface FetchParams {
    page: number;
    limit: number;
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
}

const authHeaders = () => ({ Authorization: getToken() });

const fetchAbandonedCheckouts = async (params: FetchParams) => {
    const query: Record<string, any> = { page: params.page, limit: params.limit };
    if (params.status)   query.status   = params.status;
    if (params.search)   query.search   = params.search;
    if (params.dateFrom) query.dateFrom = params.dateFrom;
    if (params.dateTo)   query.dateTo   = params.dateTo;

    const { data } = await axios.get(Apis.GET_ABANDONED_CHECKOUTS, {
        params: query,
        headers: authHeaders(),
    });
    return data as { records: AbandonedCheckoutRecord[]; total: number; page: number; limit: number; counts: CheckoutCounts };
};

export const useCaptureAbandonedCheckout = () => {
    const { mutate } = useMutation(
        (payload: { email: string; phone?: string; source?: string; plan?: string }) =>
            axios.post(Apis.CAPTURE_ABANDONED_CHECKOUT, payload),
        { onError: (err: any) => console.error("Failed to capture abandoned checkout:", err?.message) }
    );
    return { capture: mutate };
};

export const useGetAbandonedCheckouts = (params: FetchParams) => {
    const { data, isLoading, refetch } = useQuery(
        ["abandonedCheckouts", params],
        () => fetchAbandonedCheckouts(params),
        { keepPreviousData: true }
    );
    return {
        records: data?.records ?? [],
        total: data?.total ?? 0,
        counts: data?.counts ?? { initiated: 0, abandoned: 0, converted: 0, marketingConverted: 0, organicConverted: 0, marketingReached: 0, optedOut: 0, total: 0, conversionRate: "0.0" },
        isLoading,
        refetch,
    };
};

export const useExportAbandonedCheckoutsCSV = () => {
    const exportCSV = async () => {
        const response = await axios.get(Apis.EXPORT_ABANDONED_CHECKOUTS, {
            headers: authHeaders(),
            responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `abandoned-checkouts-${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };
    return { exportCSV };
};

export const useResendCheckout = () => {
    const queryClient = useQueryClient();
    return useMutation(
        ({ id, stage }: { id: number; stage: CheckoutStage }) =>
            axios.post(`${Apis.RESEND_ABANDONED_CHECKOUT}/${id}/resend`, { stage }, { headers: authHeaders() }),
        {
            onSuccess: () => queryClient.invalidateQueries(["abandonedCheckouts"]),
            onError: (err: any) => console.error("Resend failed:", err?.message),
        }
    );
};

export const useMarkCheckoutConverted = () => {
    const queryClient = useQueryClient();
    return useMutation(
        (id: number) =>
            axios.patch(`${Apis.CONVERT_ABANDONED_CHECKOUT}/${id}/convert`, {}, { headers: authHeaders() }),
        {
            onSuccess: () => queryClient.invalidateQueries(["abandonedCheckouts"]),
            onError: (err: any) => console.error("Mark converted failed:", err?.message),
        }
    );
};

export const useDeleteAbandonedCheckout = () => {
    const queryClient = useQueryClient();
    return useMutation(
        (id: number) =>
            axios.delete(`${Apis.DELETE_ABANDONED_CHECKOUT}/${id}`, { headers: authHeaders() }),
        {
            onSuccess: () => queryClient.invalidateQueries(["abandonedCheckouts"]),
            onError: (err: any) => console.error("Delete failed:", err?.message),
        }
    );
};

export const useToggleCheckoutOptIn = () => {
    const queryClient = useQueryClient();
    return useMutation(
        (id: number) =>
            axios.patch(`${Apis.TOGGLE_OPT_IN_CHECKOUT}/${id}/opt-in`, {}, { headers: authHeaders() }),
        {
            onSuccess: () => queryClient.invalidateQueries(["abandonedCheckouts"]),
            onError: (err: any) => console.error("Toggle opt-in failed:", err?.message),
        }
    );
};
